import { useEffect, useState, useCallback } from "react";
import {
  Trash2, Loader2, Eye, Download, FileSpreadsheet, FileText, Mail, Phone,
  Link2, CheckCircle2, XCircle, Check,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import FilePreviewModal from "@/components/admin/FilePreviewModal";
import { useBulkSelect } from "@/hooks/useBulkSelect";

function LinkPicker({ doc, clients, allCases, onLinked }) {
  const [editing, setEditing] = useState(false);
  const [clientEmail, setClientEmail] = useState(doc.linked_client_email || "");
  const [caseId, setCaseId] = useState(doc.linked_case_id || "");
  const [saving, setSaving] = useState(false);

  const clientCases = allCases.filter((c) => c.client_email === clientEmail);

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/documents/${doc.id}/link`, {
        client_email: clientEmail || null,
        case_id: caseId || null,
      });
      onLinked(data);
      toast.success("Submission linked");
      setEditing(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not link submission");
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className={`text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1.5 transition ${
          doc.linked_client_email
            ? "border-emerald-400/30 text-emerald-400 hover:border-emerald-400/50"
            : "border-amber-400/30 text-amber-400 hover:border-amber-400/50"
        }`}
        data-testid={`document-link-badge-${doc.id}`}
      >
        <Link2 size={12} />
        {doc.linked_client_email ? `Linked: ${doc.linked_client_email}` : "Unlinked"}
      </button>
    );
  }

  return (
    <div className="bg-white/5 rounded-lg p-3 space-y-2" data-testid={`document-link-form-${doc.id}`}>
      <div className="flex flex-wrap gap-2">
        <select
          value={clientEmail}
          onChange={(e) => { setClientEmail(e.target.value); setCaseId(""); }}
          className="input-dark text-xs w-auto py-1.5"
          data-testid={`document-link-client-select-${doc.id}`}
        >
          <option value="">— No client —</option>
          {clients.map((c) => <option key={c.email} value={c.email}>{c.email}</option>)}
        </select>
        <select
          value={caseId}
          onChange={(e) => setCaseId(e.target.value)}
          disabled={!clientEmail}
          className="input-dark text-xs w-auto py-1.5 disabled:opacity-40"
          data-testid={`document-link-case-select-${doc.id}`}
        >
          <option value="">— No application —</option>
          {clientCases.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="btn-gold text-xs py-1.5 px-3"
          data-testid={`document-link-save-${doc.id}`}
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
        </button>
        <button type="button" onClick={() => setEditing(false)} className="text-xs text-white/50 hover:text-white">
          Cancel
        </button>
      </div>
    </div>
  );
}

function FileRow({ doc, file, onDecided, onPreview, onDownload }) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  async function decide(decision, reasonText) {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/documents/${doc.id}/files/${file.id}/decision`, {
        decision,
        reason: reasonText || null,
      });
      onDecided(data);
      toast.success(decision === "approved" ? "File approved — client notified" : "File rejected — client notified");
      setRejecting(false);
      setReason("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save decision");
    } finally {
      setBusy(false);
    }
  }

  const STATUS_META = {
    pending: { label: "Pending", className: "border-white/15 text-white/50" },
    approved: { label: "Approved", className: "border-emerald-400/30 text-emerald-400" },
    rejected: { label: "Rejected", className: "border-red-400/30 text-red-400" },
  };
  const meta = STATUS_META[file.status] || STATUS_META.pending;

  return (
    <div className="bg-white/5 rounded-lg px-3 py-2" data-testid={`document-file-${file.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-white/70 min-w-0">
          <span className="truncate">{file.label ? `${file.label}: ${file.original_name}` : file.original_name}</span>
          <span className={`shrink-0 px-2 py-0.5 rounded-full border ${meta.className}`}>{meta.label}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={onPreview}
            className="w-6 h-6 grid place-items-center rounded-full hover:text-[var(--gold)] hover:bg-white/10 transition"
            title="Preview"
            data-testid={`document-preview-${file.id}`}
          >
            <Eye size={12} />
          </button>
          <button
            onClick={onDownload}
            className="w-6 h-6 grid place-items-center rounded-full hover:text-[var(--gold)] hover:bg-white/10 transition"
            title="Download"
            data-testid={`document-download-${file.id}`}
          >
            <Download size={12} />
          </button>
          <button
            onClick={() => decide("approved")}
            disabled={busy}
            className="w-6 h-6 grid place-items-center rounded-full text-white/40 hover:text-emerald-400 hover:bg-emerald-500/10 transition disabled:opacity-40"
            title="Approve"
            data-testid={`document-approve-${file.id}`}
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
          </button>
          <button
            onClick={() => setRejecting((r) => !r)}
            disabled={busy}
            className="w-6 h-6 grid place-items-center rounded-full text-white/40 hover:text-red-400 hover:bg-red-500/10 transition disabled:opacity-40"
            title="Reject"
            data-testid={`document-reject-${file.id}`}
          >
            <XCircle size={12} />
          </button>
        </div>
      </div>
      {file.status === "rejected" && file.rejection_reason && !rejecting && (
        <p className="text-xs text-red-400/80 mt-1.5">Reason: {file.rejection_reason}</p>
      )}
      {rejecting && (
        <div className="mt-2 flex flex-col gap-1.5" data-testid={`document-reject-form-${file.id}`}>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Reason for rejection (required)"
            className="input-dark text-xs"
            data-testid={`document-reject-reason-${file.id}`}
          />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => decide("rejected", reason)}
              disabled={busy || !reason.trim()}
              className="btn-outline-gold text-xs py-1.5 px-3 disabled:opacity-40"
              data-testid={`document-reject-confirm-${file.id}`}
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : "Confirm rejection"}
            </button>
            <button
              type="button"
              onClick={() => { setRejecting(false); setReason(""); }}
              className="text-xs text-white/50 hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SubmissionActions({ doc, onDecided }) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(null);

  async function decideAll(decision, reasonText) {
    setBusy(decision);
    try {
      const { data } = await api.put(`/admin/documents/${doc.id}/decision`, {
        decision,
        reason: reasonText || null,
      });
      onDecided(data);
      toast.success(decision === "approved" ? "All files approved — client notified" : "All files rejected — client notified");
      setRejecting(false);
      setReason("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save decision");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => decideAll("approved")}
          disabled={busy !== null}
          className="text-xs px-2.5 py-1.5 rounded-full border border-emerald-400/25 text-emerald-400 hover:bg-emerald-500/10 transition inline-flex items-center gap-1 disabled:opacity-40"
          data-testid={`document-approve-all-${doc.id}`}
        >
          {busy === "approved" ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approve All
        </button>
        <button
          type="button"
          onClick={() => setRejecting((r) => !r)}
          disabled={busy !== null}
          className="text-xs px-2.5 py-1.5 rounded-full border border-red-400/25 text-red-400 hover:bg-red-500/10 transition inline-flex items-center gap-1 disabled:opacity-40"
          data-testid={`document-reject-all-${doc.id}`}
        >
          <XCircle size={12} /> Reject All
        </button>
      </div>
      {rejecting && (
        <div className="mt-2 flex flex-col gap-1.5 max-w-sm" data-testid={`document-reject-all-form-${doc.id}`}>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Reason for rejecting all files (required)"
            className="input-dark text-xs"
            data-testid={`document-reject-all-reason-${doc.id}`}
          />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => decideAll("rejected", reason)}
              disabled={busy !== null || !reason.trim()}
              className="btn-outline-gold text-xs py-1.5 px-3 disabled:opacity-40"
              data-testid={`document-reject-all-confirm-${doc.id}`}
            >
              {busy === "rejected" ? <Loader2 size={12} className="animate-spin" /> : "Confirm rejection"}
            </button>
            <button
              type="button"
              onClick={() => { setRejecting(false); setReason(""); }}
              className="text-xs text-white/50 hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DocumentsPanel({ isAdmin, highlightSubmissionId, documents, setDocuments, loadingDocs }) {
  const [exportingFormat, setExportingFormat] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [clients, setClients] = useState([]);
  const [allCases, setAllCases] = useState([]);
  const bulkSelect = useBulkSelect();

  const loadClients = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/clients");
      setClients(data.items || []);
    } catch {
      // non-critical — link picker just won't show client options
    }
  }, []);

  const loadCases = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/cases");
      setAllCases(data.items || []);
    } catch {
      // non-critical — link picker just won't show application options
    }
  }, []);

  useEffect(() => { loadClients(); loadCases(); }, [loadClients, loadCases]);

  useEffect(() => {
    if (!highlightSubmissionId || documents.length === 0) return;
    document.getElementById(`document-${highlightSubmissionId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightSubmissionId, documents]);

  function replaceDoc(updated) {
    setDocuments((docs) => docs.map((d) => (d.id === updated.id ? updated : d)));
  }

  async function downloadFile(submissionId, fileId, originalName) {
    try {
      const res = await api.get(`/admin/documents/${submissionId}/files/${fileId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = originalName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  }

  async function exportDocuments(format) {
    setExportingFormat(format);
    try {
      const res = await api.get(`/admin/documents/export?format=${format}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `document-submissions.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    } finally {
      setExportingFormat(null);
    }
  }

  async function deleteDocument(id) {
    if (!window.confirm("Delete this submission and all its files?")) return;
    try {
      await api.delete(`/admin/documents/${id}`);
      setDocuments((s) => s.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  }

  async function bulkDeleteDocuments() {
    const ids = [...bulkSelect.selected];
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected submission(s) and all their files? This cannot be undone.`)) return;
    try {
      await api.post("/admin/documents/bulk-delete", { ids });
      setDocuments((s) => s.filter((x) => !ids.includes(x.id)));
      bulkSelect.clear();
      toast.success(`Deleted ${ids.length} submission(s)`);
    } catch {
      toast.error("Bulk delete failed");
    }
  }

  return (
    <div className="space-y-3" data-testid="admin-documents-panel">
      <div className="flex justify-end gap-2 mb-1">
        <button
          onClick={() => exportDocuments("xlsx")}
          disabled={exportingFormat !== null || documents.length === 0}
          className="btn-outline-gold text-sm py-2 px-4 disabled:opacity-40"
          data-testid="admin-export-xlsx"
        >
          {exportingFormat === "xlsx" ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
          Export Excel
        </button>
        <button
          onClick={() => exportDocuments("pdf")}
          disabled={exportingFormat !== null || documents.length === 0}
          className="btn-outline-gold text-sm py-2 px-4 disabled:opacity-40"
          data-testid="admin-export-pdf"
        >
          {exportingFormat === "pdf" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
          Export PDF
        </button>
      </div>
      {loadingDocs && <div className="text-white/55 text-center py-12"><Loader2 size={24} className="text-[var(--gold)] animate-spin mx-auto" /></div>}
      {!loadingDocs && documents.length === 0 && (
        <div className="card-dark p-12 text-center text-white/55" data-testid="admin-no-documents">
          No document submissions yet. They'll appear here as clients upload files.
        </div>
      )}
      {isAdmin && !loadingDocs && documents.length > 0 && (
        <div className="flex items-center gap-3 mb-1" data-testid="admin-documents-bulk-bar">
          <label className="flex items-center gap-2 text-xs text-white/55 cursor-pointer select-none">
            <input
              type="checkbox"
              className="w-4 h-4 accent-[var(--gold)] cursor-pointer"
              checked={bulkSelect.isAllSelected(documents.map((d) => d.id))}
              onChange={() => bulkSelect.toggleAll(documents.map((d) => d.id))}
              data-testid="admin-documents-select-all"
            />
            Select all
          </label>
          {bulkSelect.selected.size > 0 && (
            <button
              onClick={bulkDeleteDocuments}
              className="text-xs px-3 py-1.5 rounded-full border border-red-400/30 text-red-400 hover:bg-red-500/10 transition inline-flex items-center gap-1.5"
              data-testid="admin-documents-bulk-delete"
            >
              <Trash2 size={12} /> Delete Selected ({bulkSelect.selected.size})
            </button>
          )}
        </div>
      )}
      {documents.map((doc) => {
        const pendingCount = doc.files.filter((f) => f.status === "pending").length;
        return (
        <div
          key={doc.id}
          id={`document-${doc.id}`}
          className={`card-dark p-5 flex gap-3 ${doc.id === highlightSubmissionId ? "ring-2 ring-[var(--gold)]" : ""}`}
          data-testid={`document-row-${doc.id}`}
        >
          {isAdmin && (
            <input
              type="checkbox"
              className="w-4 h-4 mt-1 accent-[var(--gold)] cursor-pointer shrink-0"
              checked={bulkSelect.selected.has(doc.id)}
              onChange={() => bulkSelect.toggle(doc.id)}
              data-testid={`document-select-${doc.id}`}
            />
          )}
          <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <h3 className="font-semibold text-white">{doc.name}</h3>
              <div className="flex flex-wrap items-center gap-3 text-xs text-white/55 mt-1">
                <a href={`mailto:${doc.email}`} className="flex items-center gap-1 hover:text-[var(--gold)]">
                  <Mail size={12} /> {doc.email}
                </a>
                {doc.phone && <span className="flex items-center gap-1"><Phone size={12} /> {doc.phone}</span>}
                {doc.service_type && (
                  <span className="px-2 py-0.5 rounded-full border border-[var(--gold)]/25 text-[var(--gold)]">
                    {doc.service_type}
                  </span>
                )}
                <span
                  className={`px-2 py-0.5 rounded-full border ${
                    pendingCount > 0 ? "border-amber-400/30 text-amber-300" : "border-emerald-400/25 text-emerald-400"
                  }`}
                  data-testid={`document-pending-count-${doc.id}`}
                >
                  {pendingCount > 0 ? `${pendingCount} pending` : "All reviewed"}
                </span>
              </div>
              <div className="mt-2">
                <LinkPicker doc={doc} clients={clients} allCases={allCases} onLinked={replaceDoc} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/45">{new Date(doc.created_at).toLocaleString()}</span>
              {isAdmin && (
                <button
                  onClick={() => deleteDocument(doc.id)}
                  className="w-8 h-8 grid place-items-center rounded-full text-white/40 hover:text-red-400 hover:bg-red-500/10 transition"
                  title="Delete"
                  data-testid={`document-delete-${doc.id}`}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
          {doc.message && <p className="text-sm text-white/70 leading-relaxed whitespace-pre-wrap mb-3">{doc.message}</p>}
          <div className="mb-3">
            <SubmissionActions doc={doc} onDecided={replaceDoc} />
          </div>
          <div className="space-y-2">
            {doc.files.map((f) => (
              <FileRow
                key={f.id}
                doc={doc}
                file={f}
                onDecided={replaceDoc}
                onPreview={() => setPreviewFile({ submissionId: doc.id, file: f })}
                onDownload={() => downloadFile(doc.id, f.id, f.original_name)}
              />
            ))}
          </div>
          </div>
        </div>
        );
      })}
      {previewFile && (
        <FilePreviewModal
          submissionId={previewFile.submissionId}
          file={previewFile.file}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </div>
  );
}
